import {
	MutationCache,
	QueryCache,
	QueryClient,
	skipToken,
} from '@tanstack/react-query';
import type { QueryClientConfig } from '@tanstack/react-query';
import {
	ensureDemoSandboxFromStore,
	getDemoQueryResult,
	hasLocalDemoMutations,
	isDemoSandboxActive,
	isPassthroughDemoQueryKey,
	markDemoQueriesMutated,
	persistDemoQueryResult,
} from './demoCompanySandbox';

const SANDBOX_WRAPPED = '__demoSandboxWrapped';

function wrapSandboxQueryFn(queryFn: any, queryKey: unknown) {
	if (!queryFn || queryFn === skipToken || queryFn[SANDBOX_WRAPPED]) {
		return queryFn;
	}

	const wrapped = async (context: any) => {
		await ensureDemoSandboxFromStore();
		if (!isDemoSandboxActive() || isPassthroughDemoQueryKey(queryKey)) {
			return queryFn(context);
		}

		const pageParam =
			context && typeof context === 'object' && 'pageParam' in context
				? context.pageParam
				: undefined;
		const cached = getDemoQueryResult(context?.queryKey ?? queryKey, pageParam);

		// Pre-mutation: serve the cloned remote snapshot offline.
		// Post-mutation: always re-run queryFn so axios adapters hit patched baselines.
		// (Returning stale active.queries here made tasks/WPs appear to "not persist".)
		if (!hasLocalDemoMutations() && cached !== undefined) {
			return cached;
		}

		try {
			const data = await queryFn(context);
			persistDemoQueryResult(context?.queryKey ?? queryKey, data, pageParam);
			return data;
		} catch (error) {
			if (hasLocalDemoMutations() && cached !== undefined) {
				return cached;
			}
			throw error;
		}
	};

	wrapped[SANDBOX_WRAPPED] = true;
	return wrapped;
}

function wrapSandboxMutationFn(mutationFn: any) {
	if (!mutationFn || mutationFn[SANDBOX_WRAPPED]) {
		return mutationFn;
	}

	const wrapped = async (variables: any, context: any) => {
		await ensureDemoSandboxFromStore();
		const result = await mutationFn(variables, context);
		if (isDemoSandboxActive()) {
			markDemoQueriesMutated();
		}
		return result;
	};

	wrapped[SANDBOX_WRAPPED] = true;
	return wrapped;
}

class DemoSandboxQueryClient extends QueryClient {
	defaultQueryOptions(options: any) {
		const defaulted = super.defaultQueryOptions(options) as any;
		if (defaulted.queryFn === skipToken) {
			return defaulted;
		}
		return {
			...defaulted,
			queryFn: wrapSandboxQueryFn(defaulted.queryFn, defaulted.queryKey),
		};
	}

	defaultMutationOptions(options?: any) {
		const defaulted = super.defaultMutationOptions(options) as any;
		return {
			...defaulted,
			mutationFn: wrapSandboxMutationFn(defaulted.mutationFn),
		};
	}
}

export function createQueryClientWithDemoSandbox(
	config: QueryClientConfig = {}
): QueryClient {
	const client = new DemoSandboxQueryClient({
		...config,
		queryCache:
			config.queryCache ||
			new QueryCache({
				onSuccess: (data, query) => {
					if (!isDemoSandboxActive()) return;
					persistDemoQueryResult(query.queryKey, data);
				},
			}),
		mutationCache:
			config.mutationCache ||
			new MutationCache({
				onSuccess: () => {
					if (!isDemoSandboxActive()) return;
					markDemoQueriesMutated();
				},
			}),
	});

	client.getQueryCache().subscribe((event) => {
		if (!isDemoSandboxActive() || event.type !== 'updated') return;
		const data = event.query?.state?.data;
		if (data === undefined) return;
		if (isPassthroughDemoQueryKey(event.query.queryKey)) return;
		persistDemoQueryResult(event.query.queryKey, data);
	});

	return client;
}

export { wrapSandboxQueryFn, wrapSandboxMutationFn };
